import { Box, Card, CardContent, Skeleton, Switch, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useCan } from "../../../auth/permissions.js";
import { toast } from "../../../components/ui/use-toast.js";
import { ApiError } from "../../../lib/api.js";
import { SectionCardHeader } from "../shared.js";
import { marketingApi } from "../client.js";

const SETTINGS_KEY = ["marketing", "agent-settings"] as const;
const PLATFORMS_KEY = ["marketing", "platforms"] as const;

/**
 * The master "go live" switch for autonomous publishing. Flipping it on lets
 * the publisher post on schedule — but only for accounts that are connected AND
 * have their per-account auto-publish toggle on (and only drafts opted in via
 * Approve). We surface that pre-flight count so the operator knows what will
 * actually go out.
 */
export function PublishingControlsPanel(): JSX.Element {
  const qc = useQueryClient();
  // The master autonomous-publishing switch is configuration, gated on
  // marketing:manage (NOT marketing:edit). Read-only viewers — including a
  // marketing_manager without manage — see the state but the switch is disabled.
  const canEdit = useCan("marketing:manage");
  const settings = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: marketingApi.getAgentSettings,
    retry: 0,
  });
  const accounts = useQuery({
    queryKey: PLATFORMS_KEY,
    queryFn: marketingApi.listPlatformAccounts,
    retry: 0,
  });

  const setEnabled = useMutation({
    mutationFn: (enabled: boolean) => marketingApi.setPublisherEnabled(enabled),
    onSuccess: (next) => {
      void qc.invalidateQueries({ queryKey: SETTINGS_KEY });
      toast({
        title: next.publisher_enabled
          ? "Automated publishing on"
          : "Automated publishing off (safe mode)",
      });
    },
    onError: (e) =>
      toast({
        variant: "destructive",
        title: "Couldn't update",
        description: e instanceof ApiError ? e.message : "Please retry.",
      }),
  });

  const enabled = !!settings.data?.publisher_enabled;
  const ready = (accounts.data ?? []).filter(
    (a) => a.status === "connected" && a.publish_enabled,
  );

  return (
    <Card>
      <SectionCardHeader
        title="Automated publishing"
        subtitle="The master switch for autonomous posting on schedule."
      />
      <CardContent sx={{ px: 2.5, py: 2 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "text.primary" }}>
                {enabled ? "Publishing is ON" : "Publishing is OFF"}
              </Typography>
              <Box
                component="span"
                data-testid="publisher-state"
                sx={[
                  { borderRadius: 999, px: 1, py: 0.25, fontSize: 11, fontWeight: 500 },
                  enabled
                    ? { bgcolor: (t) => alpha(t.palette.success.main, 0.16), color: "success.main" }
                    : { bgcolor: "action.hover", color: "text.secondary" },
                ]}
              >
                {enabled ? "live" : "safe mode"}
              </Box>
            </Box>
            <Typography sx={{ mt: 0.5, fontSize: 14, color: "text.secondary" }}>
              {ready.length > 0 ? (
                <Box component="span" data-testid="publisher-ready">
                  {ready.length} account{ready.length === 1 ? "" : "s"} ready to
                  publish ({ready.map((a) => a.account_name).join(", ")}).
                </Box>
              ) : (
                <Box component="span" data-testid="publisher-ready">
                  No accounts are ready yet — connect an account and turn on its
                  Auto-publish toggle first.
                </Box>
              )}
            </Typography>
          </Box>
          <Switch
            checked={enabled}
            disabled={!canEdit || setEnabled.isPending}
            onChange={(_, checked) => setEnabled.mutate(checked)}
            inputProps={{ "aria-label": "Automated publishing" }}
          />
        </Box>

        <Typography
          sx={{
            mt: 2,
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            bgcolor: "surfaceSoft",
            p: 1.5,
            fontSize: 12,
            color: "text.secondary",
          }}
        >
          Safety: even when this is ON, a draft only posts if its account is
          connected + auto-publish-enabled and the draft was approved for
          auto-publishing. Anything missing a credential stays in safe mode (logs
          only, no real post).
        </Typography>
      </CardContent>
    </Card>
  );
}
